import CustomButton from "@components/buttons/CustomButton";
import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import SlidingPanel from "@components/sliders/SlidingPanel";
import { ownersApiService } from "@services/apis/Owners";
import { isOwnersApiFailure } from "@services/models/Owners/OwnersModels";
import React, { useMemo, useState } from "react";
import useSnackbarMessage from "../../../utils/HandleSuccessErrorSnackbars";

interface AddOwnerIpWhitelistSliderProps {
  isOpen: boolean;
  ownerId: number | null;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddOwnerIpWhitelistSlider: React.FC<AddOwnerIpWhitelistSliderProps> = ({
  isOpen,
  ownerId,
  onClose,
  onSuccess,
}) => {
  const { handleError, handleSuccess } = useSnackbarMessage();

  const [address, setAddress] = useState("");
  const [partnerMerchantId, setPartnerMerchantId] = useState("");
  const [label, setLabel] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const canSubmit = useMemo(
    () => !!ownerId && address.trim().length > 0,
    [ownerId, address],
  );

  const resetForm = () => {
    setAddress("");
    setPartnerMerchantId("");
    setLabel("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!ownerId) {
      handleError({ message: "ownerId is required" });
      return;
    }
    if (!address.trim()) {
      handleError({ message: "address is required" });
      return;
    }

    const merchantIdNum = partnerMerchantId.trim()
      ? Number(partnerMerchantId.trim())
      : undefined;

    if (
      partnerMerchantId.trim().length > 0 &&
      (Number.isNaN(merchantIdNum) || merchantIdNum === undefined)
    ) {
      handleError({ message: "partnerMerchantId must be a number" });
      return;
    }

    try {
      setIsLoading(true);
      const res = await ownersApiService.addOwnerIpWhitelistEntry({
        ownerId,
        address: address.trim(),
        partnerMerchantId: merchantIdNum,
        label: label.trim() || undefined,
      });

      if (isOwnersApiFailure(res)) {
        handleError({ message: res.msg || "Failed to add IP to whitelist" });
        return;
      }
      handleSuccess({ message: res.msg || "IP added to whitelist" });
      onSuccess?.();
      handleClose();
    } catch (err: any) {
      handleError({
        message: err?.response?.data?.msg || "Failed to add IP to whitelist",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SlidingPanel
      isOpen={isOpen}
      heading="Add IP to whitelist"
      onClose={handleClose}
      onClickCross={handleClose}
      showConfirm={false}
    >
      <div className="flex flex-col px-[16px] py-[16px] gap-4">
        <GlobalTextInputField
          title="IP Address"
          placeholder="IPv4 / IPv6"
          value={address}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setAddress(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Partner Merchant ID (optional)"
          placeholder="e.g. 123"
          value={partnerMerchantId}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setPartnerMerchantId(e.target.value)
          }
        />
        <GlobalTextInputField
          title="Label (optional)"
          placeholder="e.g. Office VPN"
          value={label}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setLabel(e.target.value)
          }
        />
        <CustomButton
          text="ADD IP"
          onClick={handleSubmit}
          isActive={canSubmit}
          isButtonLoading={isLoading}
        />
      </div>
    </SlidingPanel>
  );
};

export default AddOwnerIpWhitelistSlider;
